import { useEffect, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Query, QueryCacheNotifyEvent } from '@tanstack/query-core';

import { networkOnlyRead, withForcedApiRefresh } from '@/api/client';
import { setMutationRevisionRefresh } from '@/api/mutation';
import { dataRevisionManifest } from '@/api/resources';
import {
  DATA_DOMAINS,
  isPublicKey,
  resourceCoherencyFromMeta,
} from '@/api/resource';
import type { DataDomain, DataRevisionManifest, DataRevisionStamp } from '@/api/types';
import { clearPrivateQueryCache, queryClient } from '@/cache/queryClient';
import { isPublicPath, isSessionPublicPath } from '@/lib/public-paths';

const BROADCAST_CHANNEL_NAME = 'shubert-data-revisions-v1';
const ROUTE_CHECK_THROTTLE_MS = 5_000;
const KNOWN_DOMAINS = new Set<DataDomain>(DATA_DOMAINS);

export type GlobalRefreshPhase =
  | 'idle'
  | 'checking'
  | 'refreshing'
  | 'partial_error'
  | 'error'
  | 'scope_changed';

export interface RefreshAttemptSummary {
  total: number;
  succeeded: number;
  failed: number;
}

export interface GlobalRefreshState {
  phase: GlobalRefreshPhase;
  lastCheckedAt: number | null;
  lastRefreshedAt: number | null;
  error: string | null;
  lastAttempt: RefreshAttemptSummary | null;
}

const IDLE_STATE: GlobalRefreshState = {
  phase: 'idle',
  lastCheckedAt: null,
  lastRefreshedAt: null,
  error: null,
  lastAttempt: null,
};

let refreshState = IDLE_STATE;
const stateListeners = new Set<() => void>();

function errorMessage(error: unknown): string {
  return String((error as Error)?.message ?? error);
}

function attemptSummary(
  attempt: ManualRefreshResult | null,
): RefreshAttemptSummary | null {
  return attempt
    ? { total: attempt.total, succeeded: attempt.succeeded, failed: attempt.failed }
    : null;
}

function setRefreshState(patch: Partial<GlobalRefreshState>): void {
  refreshState = { ...refreshState, ...patch };
  for (const listener of stateListeners) listener();
}

export function getGlobalRefreshState(): GlobalRefreshState {
  return refreshState;
}

export function subscribeGlobalRefreshState(listener: () => void): () => void {
  stateListeners.add(listener);
  return () => stateListeners.delete(listener);
}

export function useGlobalRefreshState(): GlobalRefreshState {
  return useSyncExternalStore(
    subscribeGlobalRefreshState,
    getGlobalRefreshState,
    getGlobalRefreshState,
  );
}

export interface ManifestComparison {
  scopeChanged: boolean;
  changedDomains: DataDomain[];
}

export type RevisionOrder = 'equal' | 'newer' | 'older' | 'incomparable';

function revisionMap(manifest: DataRevisionStamp): Map<DataDomain, number> {
  const revisions = new Map<DataDomain, number>();
  for (const row of manifest.domains) revisions.set(row.domain, row.revision);
  return revisions;
}

/** Equality deliberately excludes observed/updated timestamps. */
export function compareRevisionManifests(
  previous: DataRevisionStamp,
  next: DataRevisionStamp,
): ManifestComparison {
  if (previous.scope_fingerprint !== next.scope_fingerprint) {
    return { scopeChanged: true, changedDomains: [] };
  }
  const before = revisionMap(previous);
  const after = revisionMap(next);
  const domains = new Set<DataDomain>([...before.keys(), ...after.keys()]);
  const changedDomains = [...domains]
    .filter((domain) => before.get(domain) !== after.get(domain))
    .sort();
  return { scopeChanged: false, changedDomains };
}

/**
 * Revision counters are monotonic within one authorization scope. Timestamps
 * are deliberately ignored because concurrent responses can complete out of
 * order. A changed domain set or a mixture of advances and regressions cannot
 * be ordered safely and therefore requires a fresh server check.
 */
export function classifyRevisionOrder(
  previous: DataRevisionStamp,
  next: DataRevisionStamp,
): RevisionOrder {
  if (previous.scope_fingerprint !== next.scope_fingerprint) return 'incomparable';

  const before = revisionMap(previous);
  const after = revisionMap(next);
  if (
    before.size !== after.size
    || [...before.keys()].some((domain) => !after.has(domain))
  ) {
    return 'incomparable';
  }

  let advanced = false;
  let regressed = false;
  for (const [domain, revision] of before) {
    const nextRevision = after.get(domain)!;
    if (nextRevision > revision) advanced = true;
    if (nextRevision < revision) regressed = true;
  }

  if (advanced && regressed) return 'incomparable';
  if (advanced) return 'newer';
  if (regressed) return 'older';
  return 'equal';
}

function queryUsesAnyDomain(query: Query, domains: ReadonlySet<DataDomain>): boolean {
  const coherency = resourceCoherencyFromMeta(query.meta);
  return coherency?.domains?.some((domain) => domains.has(domain)) === true;
}

function isManifestKey(key: readonly unknown[]): boolean {
  return key[0] === dataRevisionManifest.all[0]
    && key[1] === dataRevisionManifest.all[1];
}

/**
 * A route normally accepts the manifest before it starts any domain-backed
 * reads. If that first manifest request fails, however, the route deliberately
 * continues so a cold load can still succeed. When a later manifest becomes
 * the first accepted stamp, any data fetched in that unverified interval must
 * be invalidated: otherwise its revision-controlled `staleTime: Infinity`
 * could keep an older generation alive for the rest of the session.
 *
 * Limit the repair to domains that already have cached response data. The
 * ordinary cold path has no such entries yet and therefore does not issue a
 * duplicate request merely because it established its baseline manifest.
 */
function cachedDomainsBeforeInitialManifest(
  manifest: DataRevisionStamp,
): DataDomain[] {
  const allowed = new Set(manifest.domains.map((row) => row.domain));
  const cached = new Set<DataDomain>();
  for (const query of queryClient.getQueryCache().getAll()) {
    if (
      query.state.data === undefined
      || isManifestKey(query.queryKey)
      || isPublicKey(query.queryKey)
    ) {
      continue;
    }
    const coherency = resourceCoherencyFromMeta(query.meta);
    for (const domain of coherency?.domains ?? []) {
      if (allowed.has(domain)) cached.add(domain);
    }
  }
  return [...cached].sort();
}

/** Delivery freshness and flight boundaries can change without a D1 write.
 * Re-read active campaign projections once per foreground minute even when
 * the revision counters are unchanged. These endpoints read D1, never Meta.
 */
function expiredCampaignDomains(): DataDomain[] {
  const now = Date.now();
  return queryClient.getQueryCache().getAll().some((query) =>
    query.isActive() && query.state.data !== undefined
    && queryUsesAnyDomain(query, new Set<DataDomain>(['campaigns']))
    && now - query.state.dataUpdatedAt >= 60_000)
    ? ['campaigns'] : [];
}

type DomainRefreshOutcome =
  | { failed: false }
  | { failed: true; error: unknown };

function isRejected(result: PromiseSettledResult<unknown>): result is PromiseRejectedResult {
  return result.status === 'rejected';
}

/**
 * One coordinator pass: the queries a refresh re-reads, awaited together so a
 * rejection never hides a sibling's success. The pass settles its own reads
 * when it lands — every fulfilled slot leaves the retry set, every rejected
 * one is recorded under `recordedDomains` — and it does so before its caller
 * can look at the notice. While any pass is in flight the cache subscription
 * leaves the retry set alone (`settleOnRead`): a sibling landing early, or an
 * older in-flight request the pass lets finish before refetching, would
 * otherwise retire the notice under a rejection that has not landed yet.
 *
 * A rejected read that names no domain — a volatile poll — has nothing the
 * coordinator could retry it with, and a slot removed from the cache mid-pass
 * will never be read again: neither holds the notice.
 */
async function runCoordinatorPass(
  queries: readonly Query[],
  read: (query: Query) => Promise<unknown>,
  recordedDomains: (query: Query) => readonly DataDomain[],
): Promise<PromiseSettledResult<unknown>[]> {
  coordinatorPassesInFlight += 1;
  let settled: PromiseSettledResult<unknown>[];
  try {
    settled = await Promise.allSettled(queries.map(read));
  } finally {
    coordinatorPassesInFlight -= 1;
  }
  const cache = queryClient.getQueryCache();
  settled.forEach((result, index) => {
    const query = queries[index]!;
    const domains = isRejected(result) ? recordedDomains(query) : [];
    if (domains.length > 0 && cache.get(query.queryHash) === query) {
      pendingFailedReads.set(query.queryHash, domains);
    } else {
      pendingFailedReads.delete(query.queryHash);
    }
  });
  if (pendingFailedReads.size === 0) retireNotice();
  return settled;
}

/**
 * Mark every sibling in the changed domains stale, then explicitly refetch
 * only mounted dependencies. Splitting the operations prevents TanStack's
 * invalidation shortcut from hiding a failed active refetch.
 *
 * A rejected query is recorded under the domains asked for and no other — a
 * sibling domain the same query also serves was not what this refresh was
 * about. The outcome carries only the first rejection, for the caller to land
 * on; the pass has already settled every slot it read.
 */
async function refreshQueriesForDomains(
  domains: readonly DataDomain[],
): Promise<DomainRefreshOutcome> {
  if (domains.length === 0) return { failed: false };
  const selected = new Set(domains);
  const predicate = (query: Query) => queryUsesAnyDomain(query, selected);

  // A route loader/prefetch has no observer while its request is in flight, so
  // `type: 'active'` alone misses the exact destination the user is heading
  // toward. Remember every mounted OR in-flight query before invalidation; a
  // success from an older in-flight request clears `isInvalidated`, and would
  // otherwise leave that old generation fresh forever.
  const intended = new Map<string, Query>();
  for (const query of queryClient.getQueryCache().getAll()) {
    if (predicate(query) && (query.isActive() || query.state.fetchStatus !== 'idle')) {
      intended.set(query.queryHash, query);
    }
  }

  await queryClient.invalidateQueries({ predicate, refetchType: 'none' });

  // Include a query that mounted during invalidation. If it started after the
  // invalidation completed it already reads the newly accepted generation; if
  // it started before completion, this guarantees the same post-settlement
  // refresh as the pre-existing intent query above.
  for (const query of queryClient.getQueryCache().getAll()) {
    if (predicate(query) && (query.isActive() || query.state.fetchStatus !== 'idle')) {
      intended.set(query.queryHash, query);
    }
  }

  const queries = [...intended.values()];
  const settled = await runCoordinatorPass(
    queries,
    async (query) => {
      if (query.state.fetchStatus !== 'idle') {
        if (query.state.data !== undefined) {
          // A background refetch with coherent prior data can be replaced now;
          // TanStack reverts to the prior payload before starting the new call.
          return query.fetch(undefined, { cancelRefetch: true });
        }

        // Canceling a loader's first request would reject the loader promise
        // and route into the cold-load error screen. Let it settle, then fetch
        // once more under the accepted revision. The stale result can paint
        // briefly, but it cannot become the cache's final supposedly-fresh
        // generation — and, landing inside this pass, it cannot settle the
        // slot's pending read either: the refetch below is what reports.
        try {
          await query.fetch();
        } catch {
          // The guaranteed retry below is the result reported to the coordinator.
        }
        query.invalidate();
        return query.fetch();
      }

      return query.fetch();
    },
    (query) => (resourceCoherencyFromMeta(query.meta)?.domains ?? [])
      .filter((domain) => selected.has(domain)),
  );

  const failure = settled.find(isRejected);
  return failure ? { failed: true, error: failure.reason } : { failed: false };
}

interface ManualRefreshSelection {
  activeQueryHashes: Set<string>;
  domains: Set<DataDomain>;
}

interface ManualRefreshResult extends RefreshAttemptSummary {
  errors: string[];
}

function activeManualRefreshSelection(): ManualRefreshSelection {
  const activeQueryHashes = new Set<string>();
  const domains = new Set<DataDomain>();

  for (const query of queryClient.getQueryCache().getAll()) {
    if (!query.isActive() || isManifestKey(query.queryKey) || isPublicKey(query.queryKey)) {
      continue;
    }
    const coherency = resourceCoherencyFromMeta(query.meta);
    if (coherency?.immutable === true) continue;
    activeQueryHashes.add(query.queryHash);
    for (const domain of coherency?.domains ?? []) domains.add(domain);
  }

  return { activeQueryHashes, domains };
}

async function refreshManualSelection(
  selection: ManualRefreshSelection,
  additionallyChangedDomains: readonly DataDomain[],
): Promise<ManualRefreshResult> {
  const relatedDomains = new Set<DataDomain>([
    ...selection.domains,
    ...additionallyChangedDomains,
  ]);
  const predicate = (query: Query) => (
    selection.activeQueryHashes.has(query.queryHash)
    || queryUsesAnyDomain(query, relatedDomains)
  );

  // Domain siblings that are not mounted become stale but remain untouched on
  // the network. Unrelated inactive slots are not invalidated at all.
  await queryClient.invalidateQueries({ predicate, refetchType: 'none' });

  // Refetch each mounted dependency independently. `refetchQueries` rejects as
  // one aggregate and loses which siblings still succeeded; allSettled lets us
  // preserve every last coherent payload and accurately distinguish a partial
  // route refresh from a total outage. A rejected dependency is recorded under
  // every domain it serves: it was selected for being mounted, not for a domain.
  const activeQueries = queryClient.getQueryCache().getAll()
    .filter((query) => query.isActive() && predicate(query));
  const settled = await runCoordinatorPass(
    activeQueries,
    async (query) => {
      // Joining a request that began before `withForcedApiRefresh` would
      // return that request's promise, and its HTTP call could not carry
      // X-SAM-Refresh. Cancel/revert first so each selected dependency starts
      // a new request under the force scope without dropping prior coherent
      // data.
      if (query.state.fetchStatus !== 'idle') {
        await query.cancel({ silent: true, revert: true });
      }
      return query.fetch();
    },
    (query) => resourceCoherencyFromMeta(query.meta)?.domains ?? [],
  );
  const errors = settled.filter(isRejected).map((result) => errorMessage(result.reason));
  return {
    total: settled.length,
    succeeded: settled.length - errors.length,
    failed: errors.length,
    errors,
  };
}

interface RevisionBroadcastMessage {
  kind: 'revision-stamp';
  stamp: DataRevisionStamp;
  /** Declared mutation domains make a just-committed write visible even if a
   * revision reader is briefly behind the transaction. Domain names are the
   * complete payload; no ids or response data cross tabs. */
  forced_domains?: DataDomain[];
}

interface DomainBroadcastMessage {
  kind: 'domains-invalidated';
  domains: DataDomain[];
}

type IdentityBroadcastKind = 'identity-login' | 'identity-logout' | 'identity-rbac';
interface IdentityBroadcastMessage { kind: IdentityBroadcastKind }

type RevisionChannelMessage =
  | RevisionBroadcastMessage
  | DomainBroadcastMessage
  | IdentityBroadcastMessage;

let channel: BroadcastChannel | null = null;

function isDataDomain(value: unknown): value is DataDomain {
  return typeof value === 'string' && KNOWN_DOMAINS.has(value as DataDomain);
}

function isDomainList(value: unknown): value is DataDomain[] {
  return Array.isArray(value) && value.every(isDataDomain);
}

function isRevisionStamp(value: unknown): value is DataRevisionStamp {
  if (!value || typeof value !== 'object') return false;
  const stamp = value as Partial<DataRevisionStamp>;
  return typeof stamp.observed_at === 'string'
    && typeof stamp.scope_fingerprint === 'string'
    && /^[a-f0-9]{64}$/i.test(stamp.scope_fingerprint)
    && Array.isArray(stamp.domains)
    && stamp.domains.every((row) => (
      !!row
      && isDataDomain(row.domain)
      && typeof row.revision === 'number'
      && Number.isSafeInteger(row.revision)
      && row.revision >= 0
      && typeof row.updated_at === 'string'
    ));
}

function hardReload(): void {
  if (typeof window !== 'undefined') window.location.reload();
}

function onPublicPath(): boolean {
  return typeof window !== 'undefined' && isPublicPath(window.location.pathname);
}

/** The consent page: public, but it shows and decides as the signed-in person. */
function onSessionPublicPath(): boolean {
  return typeof window !== 'undefined' && isSessionPublicPath(window.location.pathname);
}

/**
 * What the top-bar control keeps showing after a manual refresh failed, until
 * every slot that attempt could not re-read has been read again. Ambient
 * checks, mutations and cross-tab refreshes pass through it without erasing
 * it — and they read it *after* their own awaits, never from a snapshot taken
 * before, because a read that landed in between may have retired it. Kept as
 * the notice itself rather than a flag so that restoring it never needs a
 * copy of the state it interrupted.
 */
interface ManualRefreshNotice {
  phase: 'partial_error' | 'error';
  error: string;
  lastAttempt: RefreshAttemptSummary | null;
}

function forgetPendingFailures(): void {
  pendingFailedReads.clear();
  manualRefreshNotice = null;
}

/**
 * A purge drops every slot the retry set refers to, so the bookkeeping and the
 * notice it backs go with it. Nothing short of a purge clears them: an identity
 * event that turns out not to purge — an RBAC signal under an unchanged scope —
 * leaves a half-failed manual refresh with its retry intact.
 */
async function purgePrivateCache(): Promise<void> {
  forgetPendingFailures();
  await clearPrivateQueryCache();
}

/**
 * Retire the notice. While the control is showing it, it can go idle now.
 * Mid-refresh the phase belongs to the operation in flight, which lands it
 * once its own awaits are done — by reading the notice then, and finding none.
 */
function retireNotice(): void {
  const notice = manualRefreshNotice;
  if (!notice) return;
  manualRefreshNotice = null;
  setRefreshState(refreshState.phase === notice.phase
    ? { phase: 'idle', error: null, lastAttempt: null }
    : { lastAttempt: null });
}

/** Where a refresh that completed lands: back on the open notice, or idle. */
function landAfterRefresh(now: number): void {
  setRefreshState(manualRefreshNotice
    ? { ...manualRefreshNotice, lastCheckedAt: now, lastRefreshedAt: now }
    : { phase: 'idle', lastCheckedAt: now, lastRefreshedAt: now, error: null });
}

/** Where a failed check or refresh lands: back on the open notice, or on its own error. */
function landOnNoticeOrError(error: unknown): void {
  setRefreshState(manualRefreshNotice ?? { phase: 'error', error: errorMessage(error) });
}

/**
 * A pending read is retired by the next successful read of that same slot,
 * whoever started it — the remount of a slot the failed refresh left
 * invalidated, a loader warming it — or by the slot leaving the cache, since a
 * slot that no longer exists shows nothing. A sibling in the same domain
 * settles nothing: it was not the read that failed, and the slot it shares a
 * domain with still shows the prior generation. A slot nobody re-read — the
 * screen navigated away — stays pending for the same reason. The coordinator
 * cannot see a remount from inside its own operations, but while one of its
 * passes is in flight the reads that land are that pass's to settle
 * (`runCoordinatorPass`). A `setQueryData` is not a read; the manifest reader
 * writes one on every check.
 */
function settleOnRead(event: QueryCacheNotifyEvent): void {
  if (pendingFailedReads.size === 0) return;
  if (event.type === 'removed') {
    if (pendingFailedReads.delete(event.query.queryHash) && pendingFailedReads.size === 0) {
      retireNotice();
    }
    return;
  }
  if (
    coordinatorPassesInFlight > 0
    || event.type !== 'updated'
    || event.action.type !== 'success'
    || event.action.manual === true
  ) {
    return;
  }
  if (pendingFailedReads.delete(event.query.queryHash) && pendingFailedReads.size === 0) {
    retireNotice();
  }
}

/**
 * Refetch the mounted queries of `domains`, then rethrow the first failure.
 * The pass records every slot it could not re-read so the next manifest check
 * retries it, and settles every slot it could; both are complete before the
 * caller lands the status control.
 */
async function refreshDomains(domains: readonly DataDomain[]): Promise<void> {
  setRefreshState({ phase: 'refreshing', error: null });
  const outcome = await refreshQueriesForDomains(domains);
  if (outcome.failed) throw outcome.error;
}

/**
 * The end of an acceptance whose refetches all succeeded. A notice with
 * nothing left to retry is retired here: that is the only way out for a manual
 * attempt whose failures named no domain — the manifest reader itself, or a
 * volatile read, which re-polls on its own cadence — since no domain read can
 * ever settle it. A notice whose slots were not all re-read stays.
 */
function landAcceptedManifest(refreshed: boolean): void {
  if (pendingFailedReads.size === 0) retireNotice();
  if (refreshed) {
    landAfterRefresh(Date.now());
  } else if (!manualRefreshNotice && refreshState.phase !== 'refreshing') {
    // Nothing moved. A manual refresh in flight lands its own phase.
    setRefreshState({ phase: 'idle', lastCheckedAt: Date.now(), error: null });
  }
}

async function respondToIdentityEvent(kind: IdentityBroadcastKind): Promise<void> {
  lastRouteCheckAt = 0;
  if (kind === 'identity-rbac') {
    await respondToRbacSignal();
    return;
  }
  acceptedManifest = null;
  await purgePrivateCache();
  // A tab on a public path — an invite being accepted, a password being reset,
  // a creative under review — has no session to re-run through the guards and
  // keeps nothing the purge did not already drop. Reloading it threw away a
  // half-typed password because a colleague signed out in another tab. The
  // consent page is the exception: it shows who is deciding, so it reloads
  // through its guard to sign-in or to the person now signed in.
  if (onPublicPath() && !onSessionPublicPath()) return;
  hardReload();
}

/**
 * A cross-tab RBAC signal says this browser's shared session may now carry a
 * different scope. The manifest's scope fingerprint is the authority on that,
 * so ask for one instead of reloading: `acceptManifest` already fails closed —
 * purge, then reload through the session and capability gates — when the
 * fingerprint moved. An unchanged fingerprint means the rows on screen are
 * still this user's, and only the identity_rbac projections (users, roles,
 * `GET /me`) need reading again. Every other outcome fails closed too: a tab
 * that cannot get a manifest, one with no accepted stamp to compare against,
 * and one whose manifest cannot be ordered and whose recovery read then fails
 * — none can prove its scope unchanged.
 */
async function respondToRbacSignal(): Promise<void> {
  if (onPublicPath()) {
    // No session, so no manifest to ask for and no scoped rows beyond what the
    // purge drops. A probe from here can only 401.
    acceptedManifest = null;
    await purgePrivateCache();
    // The consent page shows the person's role and what it allows: read both again.
    if (onSessionPublicPath()) hardReload();
    return;
  }
  if (acceptedManifest === null) {
    await failClosedOnUnverifiableScope();
    return;
  }
  // Take the acceptance without its refetch, so that the only rejection this
  // catch can see is a manifest that could not be accepted: the fetch, or the
  // recovery read behind an incomparable manifest — a domain set that changed
  // with a deploy — which the same role edit may have turned into a 401.
  let acceptance: ManifestAcceptance;
  try {
    const manifest = await fetchManifest();
    acceptance = await enqueueManifest(manifest, false, ['identity_rbac'], true);
  } catch {
    await failClosedOnUnverifiableScope();
    return;
  }
  // A changed scope, or a manifest the recovery read could not order either,
  // has already purged and is reloading.
  if (acceptance.scopeChanged) return;
  // Proven unchanged. Re-reading identity_rbac is an ordinary refresh, and its
  // failure an ordinary refresh failure: recorded for the next check and shown
  // by the control, never a reason to blow the tab away.
  try {
    await refreshDomains(acceptance.changedDomains);
  } catch (error) {
    landOnNoticeOrError(error);
    return;
  }
  landAcceptedManifest(true);
}

async function failClosedOnUnverifiableScope(): Promise<void> {
  acceptedManifest = null;
  setRefreshState({ phase: 'scope_changed', error: null });
  await purgePrivateCache();
  hardReload();
}

function receiveBroadcastMessage(value: unknown): void {
  if (!value || typeof value !== 'object') return;
  const message = value as Partial<RevisionChannelMessage>;

  if (message.kind === 'revision-stamp') {
    const revision = message as Partial<RevisionBroadcastMessage>;
    if (!isRevisionStamp(revision.stamp)) return;
    if (revision.forced_domains !== undefined && !isDomainList(revision.forced_domains)) return;
    void enqueueManifest(revision.stamp, false, revision.forced_domains ?? []).catch((error) => {
      landOnNoticeOrError(error);
    });
    return;
  }

  if (message.kind === 'domains-invalidated') {
    const domainMessage = message as Partial<DomainBroadcastMessage>;
    if (!isDomainList(domainMessage.domains)) return;
    void refreshDomains(domainMessage.domains)
      .then(() => landAfterRefresh(Date.now()))
      .catch((error) => landOnNoticeOrError(error));
    return;
  }

  if (
    message.kind === 'identity-login'
    || message.kind === 'identity-logout'
    || message.kind === 'identity-rbac'
  ) {
    void respondToIdentityEvent(message.kind);
  }
}

function ensureBroadcastChannel(): BroadcastChannel | null {
  if (channel || typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return channel;
  }
  channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
  channel.addEventListener('message', (event: MessageEvent<unknown>) => {
    receiveBroadcastMessage(event.data);
  });
  return channel;
}

function broadcast(message: RevisionChannelMessage): void {
  ensureBroadcastChannel()?.postMessage(message);
}

function broadcastStamp(stamp: DataRevisionStamp, forcedDomains: readonly DataDomain[]): void {
  broadcast({
    kind: 'revision-stamp',
    stamp,
    ...(forcedDomains.length > 0 ? { forced_domains: [...forcedDomains] } : {}),
  });
}

function broadcastDomains(domains: readonly DataDomain[]): void {
  broadcast({ kind: 'domains-invalidated', domains: [...domains] });
}

let acceptedManifest: DataRevisionStamp | null = null;
let operationQueue: Promise<unknown> = Promise.resolve();
let sessionIsEnding = false;
/**
 * Slots a refresh could not re-read, by query hash, each under the domains it
 * is retried through. Keyed by the slot and not the domain because that is the
 * granularity a read settles at: a sibling in the same domain succeeding says
 * nothing about the slot that failed (`settleOnRead`). Their domains are
 * folded into every manifest check until each slot has been read again.
 */
const pendingFailedReads = new Map<string, readonly DataDomain[]>();
/** Passes whose reads have not all landed yet. See `runCoordinatorPass`. */
let coordinatorPassesInFlight = 0;
let manualRefreshNotice: ManualRefreshNotice | null = null;

/** The domains the next manifest check retries, whatever it says has moved. */
function pendingDomains(): DataDomain[] {
  const domains = new Set<DataDomain>();
  for (const recorded of pendingFailedReads.values()) {
    for (const domain of recorded) domains.add(domain);
  }
  return [...domains].sort();
}

/**
 * A manual attempt that produced no outcome re-read nothing after its
 * acceptance advanced past `domains`, so no read settled anything: every
 * cached slot in them may still hold the generation before. Record each slot
 * by its own hash, under the domains that moved — the retry then invalidates
 * and refetches them like any other pending read, and each is settled by its
 * own read. A slot holding nothing and fetching nothing has no prior
 * generation to show.
 */
function recordUnreadSlots(domains: readonly DataDomain[]): void {
  if (domains.length === 0) return;
  const selected = new Set(domains);
  for (const query of queryClient.getQueryCache().getAll()) {
    if (
      isManifestKey(query.queryKey)
      || isPublicKey(query.queryKey)
      || (query.state.data === undefined && query.state.fetchStatus === 'idle')
    ) {
      continue;
    }
    const affected = (resourceCoherencyFromMeta(query.meta)?.domains ?? [])
      .filter((domain) => selected.has(domain));
    if (affected.length > 0) pendingFailedReads.set(query.queryHash, affected);
  }
}

interface ManifestAcceptance {
  scopeChanged: boolean;
  changedDomains: DataDomain[];
}

async function failClosedOnScopeChange(
  stamp: DataRevisionStamp,
  shouldBroadcast: boolean,
): Promise<void> {
  acceptedManifest = stamp;
  setRefreshState({ phase: 'scope_changed', error: null });
  if (shouldBroadcast) broadcastStamp(stamp, []);
  await purgePrivateCache();
  // A hard reload re-runs session and route capability gates before any old
  // screen can repaint under a changed authorization scope.
  hardReload();
}

async function failClosedOnRevisionInconsistency(): Promise<void> {
  const error = 'Data revision integrity check failed; reloading';
  setRefreshState({ phase: 'error', error });
  await purgePrivateCache();
  // An incomparable second network read means this tab cannot prove which
  // cached projection is current. Reload through the auth and route gates.
  hardReload();
}

async function acceptManifest(
  manifest: DataRevisionStamp,
  shouldBroadcast: boolean,
  forcedDomains: readonly DataDomain[] = [],
  deferDomainRefresh = false,
  recoveredIncomparable = false,
): Promise<ManifestAcceptance> {
  const previous = acceptedManifest;
  const comparison = previous
    ? compareRevisionManifests(previous, manifest)
    : { scopeChanged: false, changedDomains: [] };

  if (comparison.scopeChanged) {
    await failClosedOnScopeChange(manifest, shouldBroadcast);
    return { scopeChanged: true, changedDomains: [] };
  }

  const order = previous ? classifyRevisionOrder(previous, manifest) : 'newer';
  if (order === 'incomparable') {
    if (recoveredIncomparable) {
      await failClosedOnRevisionInconsistency();
      return { scopeChanged: true, changedDomains: [] };
    }

    // Do not enqueue this read from inside the serialized acceptance queue or
    // the queue would wait on itself. The fresh response is still evaluated
    // against the last accepted stamp before anything can replace it.
    const freshManifest = await fetchManifest();
    return acceptManifest(
      freshManifest,
      shouldBroadcast,
      forcedDomains,
      deferDomainRefresh,
      true,
    );
  }

  const changedDomains = [...new Set([
    ...(order === 'newer' ? comparison.changedDomains : []),
    ...(previous === null ? cachedDomainsBeforeInitialManifest(manifest) : []),
    ...forcedDomains,
    ...pendingDomains(),
    ...expiredCampaignDomains(),
  ])];
  // Equal and wholly older responses are legitimate races. Keep the newest
  // accepted counters (including its scope stamp) while still honoring a
  // mutation's explicitly declared domains.
  const acceptedStamp = previous && (order === 'equal' || order === 'older')
    ? previous
    : manifest;
  acceptedManifest = acceptedStamp;

  if (shouldBroadcast && (previous === null || changedDomains.length > 0)) {
    broadcastStamp(acceptedStamp, forcedDomains);
  }

  if (deferDomainRefresh) {
    return { scopeChanged: false, changedDomains };
  }

  const refreshed = changedDomains.length > 0;
  if (refreshed) await refreshDomains(changedDomains);
  landAcceptedManifest(refreshed);

  return { scopeChanged: false, changedDomains };
}

function enqueueManifest(
  manifest: DataRevisionStamp,
  shouldBroadcast: boolean,
  forcedDomains: readonly DataDomain[] = [],
  deferDomainRefresh = false,
): Promise<ManifestAcceptance> {
  const operation = operationQueue.then(() => acceptManifest(
    manifest,
    shouldBroadcast,
    forcedDomains,
    deferDomainRefresh,
  ));
  operationQueue = operation.catch(() => undefined);
  return operation;
}

async function fetchManifest(): Promise<DataRevisionManifest> {
  // Explicit checks must never join or reuse the ambient polling request: a
  // mutation and a manual force-refresh need a fresh scope/revision decision.
  const manifest = await networkOnlyRead<DataRevisionManifest>('/data-revisions');
  queryClient.setQueryData(dataRevisionManifest.key(undefined), manifest);
  return manifest;
}

async function fetchAndAcceptManifest(
  forcedDomains: readonly DataDomain[] = [],
): Promise<DataRevisionManifest> {
  const manifest = await fetchManifest();
  await enqueueManifest(manifest, true, forcedDomains);
  return manifest;
}

let lastRouteCheckAt = 0;

/** Check the revision manifest before a route's domain-backed preload. */
export async function registerRouteIntent(): Promise<void> {
  const now = Date.now();
  if (now - lastRouteCheckAt < ROUTE_CHECK_THROTTLE_MS) return;
  lastRouteCheckAt = now;
  if (!manualRefreshNotice) {
    setRefreshState({ phase: 'checking', error: null });
  }
  try {
    await fetchAndAcceptManifest();
  } catch (error) {
    landOnNoticeOrError(error);
    throw error;
  }
}

/**
 * Mutation bridge installed into `useRegisteredMutation`. Declared domains are
 * refreshed even if the manifest reader is temporarily unavailable.
 */
export async function notifyMutationSucceeded(domains: readonly DataDomain[]): Promise<void> {
  const uniqueDomains = [...new Set(domains)];
  if (sessionIsEnding) {
    sessionIsEnding = false;
    return;
  }

  if (!manualRefreshNotice) setRefreshState({ phase: 'refreshing', error: null });
  try {
    let manifest: DataRevisionManifest;
    try {
      manifest = await fetchManifest();
    } catch (manifestError) {
      // The mutation already committed. Announce its declared domains before
      // attempting this tab's refresh so a local outage cannot prevent healthy
      // sibling tabs from invalidating their own projections.
      broadcastDomains(uniqueDomains);
      await refreshDomains(uniqueDomains);
      throw manifestError;
    }
    const acceptance = await enqueueManifest(manifest, true, uniqueDomains);
    if (acceptance.scopeChanged) return;
    // The acceptance may just have re-read the domain a manual attempt failed
    // on and retired its notice; land on whatever the notice is *now*.
    landAfterRefresh(Date.now());
  } catch (error) {
    landOnNoticeOrError(error);
    throw error;
  } finally {
    if (uniqueDomains.includes('identity_rbac')) notifyRbacChanged();
  }
}

/** Force only mounted dependencies and mark their domain siblings stale. */
export async function refreshAllData(): Promise<void> {
  let attempt: ManualRefreshResult | null = null;
  let changedDomains: readonly DataDomain[] = [];
  // A failed attempt raises the notice the control keeps until every slot it
  // could not re-read has been read again. The pass recorded those slots as it
  // landed, so the next manifest check — even an equal one — retries them, and
  // only them: the rest were just read. An attempt that never produced an
  // outcome re-read nothing, so it records every slot the domains it accepted
  // could have moved. An attempt whose failures named no domain at all leaves
  // the retry set empty, and the next manifest acceptance that completes
  // cleanly retires its notice.
  const rememberFailure = (notice: ManualRefreshNotice) => {
    if (attempt === null) recordUnreadSlots(changedDomains);
    manualRefreshNotice = notice;
  };
  setRefreshState({ phase: 'refreshing', error: null, lastAttempt: null });
  try {
    attempt = await withForcedApiRefresh(async () => {
      const manifest = await fetchManifest();
      const acceptance = await enqueueManifest(manifest, true, [], true);
      if (acceptance.scopeChanged) return null;
      changedDomains = acceptance.changedDomains;

      const selection = activeManualRefreshSelection();
      setRefreshState({ phase: 'refreshing', error: null });
      return refreshManualSelection(selection, acceptance.changedDomains);
    });

    // No outcome means the acceptance failed closed and the tab is reloading.
    if (attempt === null) return;

    const now = Date.now();
    if (attempt.failed > 0) {
      const detail = attempt.errors[0] ? `: ${attempt.errors[0]}` : '';
      const message = `${attempt.failed} of ${attempt.total} active data requests failed${detail}`;
      if (attempt.succeeded > 0) {
        const notice: ManualRefreshNotice = {
          phase: 'partial_error',
          error: message,
          lastAttempt: attemptSummary(attempt),
        };
        rememberFailure(notice);
        setRefreshState({ ...notice, lastCheckedAt: now, lastRefreshedAt: now });
        return;
      }
      throw new Error(message);
    }

    setRefreshState({
      phase: 'idle',
      lastCheckedAt: now,
      lastRefreshedAt: now,
      error: null,
      lastAttempt: attemptSummary(attempt),
    });
    // The user's own refresh answered the notice. A recorded domain it did not
    // mount is an invalidated slot already, and refetches on its remount.
    forgetPendingFailures();
  } catch (error) {
    const notice: ManualRefreshNotice = {
      phase: 'error',
      error: errorMessage(error),
      lastAttempt: attemptSummary(attempt),
    };
    rememberFailure(notice);
    setRefreshState(notice);
    throw error;
  }
}

/** Announce a successful login without exposing any identity fields. */
export function notifySessionStarted(): void {
  acceptedManifest = null;
  forgetPendingFailures();
  lastRouteCheckAt = 0;
  sessionIsEnding = false;
  setRefreshState(IDLE_STATE);
  broadcast({ kind: 'identity-login' });
}

/** Announce logout; the mutation callback must not probe after the session ends. */
export function notifySessionEnded(): void {
  acceptedManifest = null;
  forgetPendingFailures();
  lastRouteCheckAt = 0;
  sessionIsEnding = true;
  broadcast({ kind: 'identity-logout' });
}

/** Announce that another tab must re-evaluate role/capability scope. */
export function notifyRbacChanged(): void {
  broadcast({ kind: 'identity-rbac' });
}

// Install once at module evaluation; hooks do not import chrome and therefore
// remain usable from public auth screens and route modules.
setMutationRevisionRefresh(notifyMutationSucceeded);
queryClient.getQueryCache().subscribe(settleOnRead);

/** Mounted only inside authenticated chrome (TopBar). */
export function useDataRevisionCoordinator(): void {
  const query = useQuery(dataRevisionManifest(undefined));

  useEffect(() => {
    ensureBroadcastChannel();
  }, []);

  useEffect(() => {
    if (!query.data) return;
    void enqueueManifest(query.data, true).catch((error) => {
      landOnNoticeOrError(error);
    });
  }, [query.data, query.dataUpdatedAt]);

  useEffect(() => {
    if (
      query.fetchStatus === 'fetching'
      && !manualRefreshNotice
      && refreshState.phase !== 'refreshing'
      && refreshState.phase !== 'scope_changed'
    ) {
      setRefreshState({ phase: 'checking', error: null });
    } else if (query.isError) {
      if (!manualRefreshNotice) {
        setRefreshState({ phase: 'error', error: errorMessage(query.error) });
      }
    }
  }, [query.error, query.fetchStatus, query.isError]);

  useEffect(() => {
    const checkWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        void registerRouteIntent().catch(() => undefined);
      }
    };
    const checkOnFocus = () => {
      void registerRouteIntent().catch(() => undefined);
    };
    document.addEventListener('visibilitychange', checkWhenVisible);
    window.addEventListener('focus', checkOnFocus);
    return () => {
      document.removeEventListener('visibilitychange', checkWhenVisible);
      window.removeEventListener('focus', checkOnFocus);
    };
  }, []);
}
